// Endpoint registry — mirrors the API contract in design doc §5.1 (extended) and
// apps/api/modules/query/router.py. Kept in sync BY HAND (ADR 0002): when an endpoint
// changes on either side, update the other in the same change.
//
// BASE_URL resolution, Moveo pattern: runtime injection → build-time env → local default.

const BASE_URL =
  (typeof window !== "undefined" && window.__env__?.REACT_APP_BASE_URL) ||
  process.env.REACT_APP_BASE_URL ||
  "http://localhost:8080";

export const API = {
  health: () => `${BASE_URL}/health`,
  meta: () => `${BASE_URL}/api/meta`,
  // industry (optional, closed vocab) scopes rows/ranks/movement to that
  // industry server-side; omitted = the general board (today's exact URL).
  rankings: ({ region = "GLOBAL", industry } = {}) =>
    `${BASE_URL}/api/rankings?region=${region}${
      industry ? `&industry=${encodeURIComponent(industry)}` : ""
    }`,
  // Field + per-industry aggregates (levels, trend, distribution, pillars,
  // monthly format mix, capital map, playbook/tempo); same optional industry
  // param semantics as rankings.
  industryStats: ({ region = "GLOBAL", industry } = {}) =>
    `${BASE_URL}/api/industry-stats?region=${region}${
      industry ? `&industry=${encodeURIComponent(industry)}` : ""
    }`,
  ciqSeries: (brandRef, query = "") =>
    `${BASE_URL}/api/brands/${brandRef}/ciq-series${query}`,
  breakdown: (brandRef, runRef) =>
    `${BASE_URL}/api/brands/${brandRef}/runs/${runRef}/breakdown`,
  drivers: (brandRef, runRef = "latest") =>
    `${BASE_URL}/api/brands/${brandRef}/runs/${runRef}/drivers`,
  brandItems: (brandRef, query = "") =>
    `${BASE_URL}/api/brands/${brandRef}/items${query}`,
  itemTypeSeries: (brandRef, typeCode, query = "") =>
    `${BASE_URL}/api/brands/${brandRef}/item-types/${typeCode}/series${query}`,
  itemTypeItems: (brandRef, typeCode, runRef = "latest") =>
    `${BASE_URL}/api/brands/${brandRef}/item-types/${typeCode}/items?run=${runRef}`,
  financials: (brandRef) => `${BASE_URL}/api/brands/${brandRef}/financials`,
  itemDetail: (itemId) => `${BASE_URL}/api/items/${itemId}`,
  itemContributions: (itemId) =>
    `${BASE_URL}/api/items/${itemId}/contributions`,
  advisor: (brandRef) => `${BASE_URL}/api/advisor/brands/${brandRef}`,
  // POST { brand, question } → SSE stream (apps/api/modules/lab/router.py);
  // consumed by labService's live miss path, never by useFetch
  labAsk: () => `${BASE_URL}/api/lab/ask`,
  // Previous-labs history: GET list (supported asks + their stored blocks,
  // newest first) / DELETE one logged lab
  labAsks: (slug) => `${BASE_URL}/api/lab/asks?brand=${slug}`,
  labAskDelete: (id) => `${BASE_URL}/api/lab/asks/${id}`,
  compare: (a, b, { region = "GLOBAL" } = {}) =>
    `${BASE_URL}/api/compare/${a}/${b}?region=${region}`,
  brands: () => `${BASE_URL}/api/brands`,
  brand: (slug) => `${BASE_URL}/api/brands/${slug}`,
  // the workspace wall (apps/api/modules/auth/router.py): POST {code,
  // device_id?} opens a brand's workspace and counts the login; POST {slug,
  // code} re-checks a stored session on load without counting
  authLogin: () => `${BASE_URL}/api/auth/login`,
  authVerify: () => `${BASE_URL}/api/auth/verify`,
  // usage tracking (apps/api/modules/track/router.py): POST {code,
  // device_id?, view, device_type} records one entry-view open, 204 at
  // once; sent keepalive by services/trackService, never awaited
  trackOpen: () => `${BASE_URL}/api/track/open`,
};

export default BASE_URL;
